import { closestCenter, DndContext, type DragEndEvent, type UniqueIdentifier } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { useCallback, useState } from 'react'
import { AddOptionInput } from '../add-option-input'
import {
  canAddDraftOption,
  changeDraftOption,
  insertDraftOption,
  moveDraftOption,
  OPTIONS_LIMIT_NOTE,
  removeDraftOption,
} from '../booking-questions-editor.domain'
import { OptionRow } from '../option-row'
import { useSortableList } from '../use-sortable-list'
import type { OptionsEditorProps } from './options-editor.interface'
import classes from './options-editor.module.scss'

const OPTIONS_LABEL = 'Options: drag to reorder'

export function OptionsEditor({ draft, errors, idPrefix, onChange }: OptionsEditorProps) {
  const [focusKey, setFocusKey] = useState<string | null>(null)
  const clearFocusRequest = useCallback(() => setFocusKey(null), [])
  const labelFor = (id: UniqueIdentifier) => {
    const value = draft.options.find((option) => option.key === id)?.value
    return value ? `option ${value}` : 'option'
  }
  const sortable = useSortableList(labelFor)
  const canAdd = canAddDraftOption(draft)
  const labelId = `${idPrefix}-options-label`

  const insertOption = (afterKey: string | null, value = '') => {
    const result = insertDraftOption(draft, afterKey, value)
    if (!result.insertedKey) return
    onChange(result.draft)
    setFocusKey(result.insertedKey)
  }

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (over) onChange(moveDraftOption(draft, String(active.id), String(over.id)))
  }

  return (
    <div className={classes.root}>
      <div id={labelId} className={classes.label}>
        {OPTIONS_LABEL}
      </div>
      <div className={classes.box}>
        <DndContext {...sortable} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={draft.options.map((option) => option.key)} strategy={verticalListSortingStrategy}>
            <ul className={classes.list} aria-labelledby={labelId}>
              {draft.options.map((option, index) => (
                <OptionRow
                  key={option.key}
                  option={option}
                  index={index}
                  error={errors.optionErrors[option.key]}
                  focusRequested={focusKey === option.key}
                  onFocused={clearFocusRequest}
                  onChange={(value) => onChange(changeDraftOption(draft, option.key, value))}
                  onEnter={() => insertOption(option.key)}
                  onRemove={() => onChange(removeDraftOption(draft, option.key))}
                />
              ))}
            </ul>
          </SortableContext>
        </DndContext>
        <AddOptionInput disabled={!canAdd} onAdd={(value) => insertOption(null, value)} />
      </div>
      {!canAdd && <div className={classes.note}>{OPTIONS_LIMIT_NOTE}</div>}
      {errors.options && (
        <div className={classes.error} role="alert">
          {errors.options}
        </div>
      )}
    </div>
  )
}
